import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { DmsAltBox, intl } from "dmsbasereact";

const FlowValue = ({ label, value, color, isLoading }) => {
    const theme = useTheme();
    const colorValue = color.includes(".")
        ? color.split(".").reduce((obj, key) => obj?.[key], theme.palette)
        : theme.palette[color]?.main || theme.palette.primary.main;

    return (
        <Box sx={{ textAlign: "center", minWidth: 80, px: 1 }}>
            <Typography
                variant="caption"
                sx={{
                    color: "text.secondary",
                    display: "block",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
            {isLoading ? (
                <Skeleton variant="text" width={50} sx={{ mx: "auto", height: 32 }} />
            ) : (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: colorValue,
                        fontSize: "1.1rem",
                    }}
                >
                    {value !== null && value !== undefined ? value.toLocaleString("pt-BR") : "--"}
                </Typography>
            )}
        </Box>
    );
};

FlowValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string,
    isLoading: PropTypes.bool,
};

const Operator = ({ symbol }) => (
    <Typography
        variant="h5"
        sx={{
            fontWeight: "bold",
            color: "text.secondary",
            mx: 0.5,
            alignSelf: "center",
        }}
    >
        {symbol}
    </Typography>
);

Operator.propTypes = {
    symbol: PropTypes.string.isRequired,
};

export const FlowEquation = ({ data, isLoading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const {
        eurofarma_total = 0,
        distributor_total = 0,
        intermediary_total = 0,
        dispenser_total = 0,
        buyer_total = 0,
        ministry_health_total = 0,
        dispensation_total = 0,
        balance = 0,
        returns = 0,
    } = data || {};

    return (
        <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Typography variant="h6">
                    {intl.get("Analytics.Flow.Title").d("Balanço Geral")}
                </Typography>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {/* Linha 1: Venda Eurofarma = Distribuidor + Intermediária + Dispensadora + M. Saúde */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: isMobile ? "wrap" : "nowrap",
                            gap: isMobile ? 1 : 0,
                            py: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <FlowValue
                            label={intl.get("Analytics.Flow.EurofarmaTotal").d("Venda Eurofarma")}
                            value={eurofarma_total}
                            color="primary.main"
                            isLoading={isLoading}
                        />
                        <Operator symbol="=" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Distributor").d("Distribuidor")}
                            value={distributor_total}
                            color="info.main"
                            isLoading={isLoading}
                        />
                        <Operator symbol="+" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Intermediary").d("Intermediária")}
                            value={intermediary_total || null}
                            color="warning.main"
                            isLoading={isLoading}
                        />
                        <Operator symbol="+" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Dispenser").d("Dispensadora")}
                            value={dispenser_total}
                            color="success.main"
                            isLoading={isLoading}
                        />
                        <Operator symbol="+" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Buyer").d("Compradora")}
                            value={buyer_total || null}
                            color="grey.600"
                            isLoading={isLoading}
                        />
                        <Operator symbol="+" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.MinistryHealth").d("M. Saúde")}
                            value={ministry_health_total || null}
                            color="secondary.main"
                            isLoading={isLoading}
                        />
                    </Box>

                    {/* Linha 2: Dispensação = Saldo + Devolução */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: isMobile ? "wrap" : "nowrap",
                            gap: isMobile ? 1 : 0,
                            py: 1,
                        }}
                    >
                        <FlowValue
                            label={intl.get("Analytics.Flow.Dispensation").d("Dispensação")}
                            value={dispensation_total}
                            color="success.dark"
                            isLoading={isLoading}
                        />
                        <Operator symbol="=" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Balance").d("Saldo")}
                            value={balance}
                            color="info.dark"
                            isLoading={isLoading}
                        />
                        <Operator symbol="+" />
                        <FlowValue
                            label={intl.get("Analytics.Flow.Returns").d("Devolução")}
                            value={returns || null}
                            color="error.main"
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

FlowEquation.propTypes = {
    data: PropTypes.shape({
        eurofarma_total: PropTypes.number,
        distributor_total: PropTypes.number,
        intermediary_total: PropTypes.number,
        dispenser_total: PropTypes.number,
        buyer_total: PropTypes.number,
        ministry_health_total: PropTypes.number,
        dispensation_total: PropTypes.number,
        balance: PropTypes.number,
        returns: PropTypes.number,
    }),
    isLoading: PropTypes.bool,
};
